import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const ClaimPage = () => {
  const { npi } = useParams();
  const navigate = useNavigate();
  const { user, isVerified, getToken } = useAuth();
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState<boolean | null>(null);
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

  useEffect(() => {
    if (!username) { setAvailable(null); return; }
    const id = setTimeout(async () => {
      setChecking(true);
      try {
        const res = await fetch(`${API_URL}/api/providers/username/${encodeURIComponent(username)}/available`);
        const data = await res.json();
        setAvailable(data.available);
      } finally {
        setChecking(false);
      }
    }, 400);
    return () => clearTimeout(id);
  }, [username]);

  const handleClaim = async () => {
    if (!user) { setError('Please sign in'); return; }
    if (!isVerified) { setError('Identity verification required to claim'); return; }
    if (!npi) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_URL}/api/providers/${npi}/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ custom_username: username || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to claim');
      setSuccess('Profile claimed successfully');
      if (data.username) {
        setTimeout(() => navigate(`/md+${data.username}`), 800);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to claim');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Claim Profile (NPI: {npi})
          </Typography>

          {!user && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Please sign in to continue.
            </Alert>
          )}

          {user && !isVerified && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Identity verification required. Verification flow will be added soon.
            </Alert>
          )}

          <TextField
            label="Custom username (optional)"
            fullWidth
            value={username}
            onChange={(e) => setUsername(e.target.value.trim().toLowerCase())}
            helperText={checking ? 'Checking availability…' : available === null ? 'Use letters, numbers, and dashes' : available ? 'Available' : 'Unavailable'}
            sx={{ mt: 1, mb: 2 }}
          />

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          <Box display="flex" gap={2}>
            <Button variant="contained" onClick={handleClaim} disabled={loading || !user || !isVerified}>
              {loading ? <CircularProgress size={20} /> : 'Claim Profile'}
            </Button>
            <Button variant="text" onClick={() => navigate(-1)}>Cancel</Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ClaimPage;
